import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { GroupProvider } from './context/GroupContext';
import { SnackbarProvider, useSnackbar } from './contexts/SnackbarContext';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import SnackbarNotification from './components/common/SnackbarNotification';
import StripeConnectWrapper from './components/StripeConnectWrapper';
import TokenExpiredPrompt from './components/TokenExpiredPrompt';
import EmailVerificationHandler from './components/EmailVerificationHandler';
import HomePage from './pages/HomePage';
import GatewayLanding from './components/GatewayLanding';
import MainLanding from './components/MainLanding';
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import Dashboard from './pages/Dashboard';
import MemberHome from './pages/MemberHome';
import CreateGroup from './pages/CreateGroup';
import GroupCreationSuccess from './pages/GroupCreationSuccess';
import SavedPage from './pages/SavedPage';
import ResetPasswordRequest from './pages/ResetPasswordRequest';
import ResetPasswordConfirm from './pages/ResetPasswordConfirm';
import GetAppPage from './pages/GetAppPage';
import AdminProfilePage from './pages/AdminProfilePage';
import MemberProfilePage from './pages/MemberProfilePage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import BottomNavigation from './components/navigation/BottomNavigation';
import AppDownloadLanding from './components/AppDownloadLanding';
import FastTimes from './components/FastTimes';
import ShadcnTest from './components/ShadcnTest';
import ModernDashboard from './components/ModernDashboard';
import DirectLinkAuth from './components/DirectLinkAuth';
import GroupClaimPage from './components/GroupClaimPage';
import GroupClaimDemo from './components/GroupClaimDemo';
import NameClaimPage from './pages/NameClaimPage';
import NameClaimSignUp from './pages/NameClaimSignUp';
import PledgeClaimPage from './pages/PledgeClaimPage';
import './styles/App.css';
import GroupPage from './pages/group/GroupPage';
import EventDetails from './pages/group/EventDetails';
import ClassDetails from './pages/group/ClassDetails';
import MembershipDetails from './pages/group/MembershipDetails';
import MembersAdminPage from './pages/group/MembersAdminPage';
import AdminMemberProfilePage from './pages/group/MemberProfilePage';
import OnboardingIntro from './pages/onboarding/OnboardingIntro';
import IdentityVerification from './pages/onboarding/IdentityVerification';
import ProfileCreation from './pages/onboarding/ProfileCreation';
import AnniversaryDatePage from './pages/onboarding/AnniversaryDatePage';
import KidsBirthday from './pages/onboarding/KidsBirthday';
import Yartzheits from './pages/onboarding/Yartzheits';
import CommunityOnboarding from './pages/CommunityOnboarding';
import SignUp from './pages/SignUp';
import Survey from './pages/Survey';
import StripeConnectSettings from './pages/StripeConnectSettings';
import GroupInvitePage from './pages/GroupInvitePage';
import FinancialsPage from './pages/FinancialsPage';
import PledgesPage from './pages/PledgesPage';
import TicketsPage from './pages/TicketsPage';
import PrayerListPage from './pages/PrayerListPage';
import ExplorePrayersPage from './pages/ExplorePrayersPage';
import FindMinyanPage from './pages/FindMinyanPage';
import MinyanSharePage from './pages/MinyanSharePage';
import TestShadcnComponents from './components/TestShadcnComponents';
import AdminSidebarTest from './components/AdminSidebarTest';
import AdminDashboard from './components/AdminDashboard';
import PricingPage from './pages/PricingPage';
import config from './config';
import EmptyDashboard from './components/EmptyDashboard';
import PageTracker from './components/PageTracker';
import ExternalRedirect from './components/ExternalRedirect';
import groupService from './api/groupService';
import PublicCommunityPage from './pages/PublicCommunityPage';
import PostDetailsPage from './pages/PostDetailsPage';
import SelectDedicationPage from './pages/SelectDedicationPage';
import ChooseChainTypePage from './pages/ChooseChainTypePage';
import WhoIsThisForPage from './pages/WhoIsThisForPage';
import TellTheStoryPage from './pages/TellTheStoryPage';
import PreviewChainPage from './pages/PreviewChainPage';
import AuthGatePage from './pages/AuthGatePage';
import ClaimSuccessPage from './pages/ClaimSuccessPage';
import CandleLightingSharePage from './pages/CandleLightingSharePage';
import ShabbatTimesPage from './pages/ShabbatTimesPage';
import CommunitySearchPage from './pages/CommunitySearchPage';

// Initialize Stripe
// Forces a real HTTP request so the dev-server proxy (/community/ → :5001) kicks in.
// React Router would otherwise handle this client-side and render the old page.
function CommunityHardRedirect() {
  useEffect(() => { window.location.replace(window.location.href); }, []);
  return null;
}

const stripePromise = loadStripe('pk_test_51S9cgzDJrqPK4pxyfv9O2belxOZtNU0z9Da0subpw6gALUddGKOv4rdOlT3EiKuJ43nfjsCkrdHHIrmxhDA1d1bl00kykw0bFP');

// Private route component to protect authenticated routes
const PrivateRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated() ? children : <Navigate to="/login" />;
};

// Group route component with direct link authentication
const GroupRoute = ({ children }) => {
  return (
    <DirectLinkAuth requireAuth={true}>
      {children}
    </DirectLinkAuth>
  );
};

// Public route component that allows access without authentication
const PublicRoute = ({ children }) => {
  return (
    <DirectLinkAuth requireAuth={false}>
      {children}
    </DirectLinkAuth>
  );
};

// Admin route component to protect admin-only routes
const AdminRoute = ({ children }) => {
  const { isAuthenticated, currentUser } = useAuth();
  return isAuthenticated() && currentUser?.role === 'admin' 
    ? children 
    : isAuthenticated() 
      ? <Navigate to="/dashboard" /> 
      : <Navigate to="/login" />;
};

// Public Event Redirect - handles /event/:eventId URLs
const PublicEventRedirect = () => {
  const { eventId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchEventGroup = async () => {
      try {
        // Fetch event details using the simple share API endpoint
        const response = await fetch(`${config.API_URL}/share/event/${eventId}`);
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            // Redirect to the full event URL with group ID (using singular 'event' to match public share route)
            window.location.replace(`/#/group/${result.data.group_id}/event/${eventId}`);
          } else {
            setError('Event not found');
            setLoading(false);
          }
        } else {
          setError('Event not found');
          setLoading(false);
        }
      } catch (err) {
        console.error('Error fetching event:', err);
        setError('Failed to load event');
        setLoading(false);
      }
    };
    
    fetchEventGroup();
  }, [eventId]);
  
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading event...</div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>{error}</div>
      </div>
    );
  }
  
  return null;
};

// Public Class Redirect - handles /class/:classId URLs  
const PublicClassRedirect = () => {
  const { classId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchClassGroup = async () => {
      try {
        // Fetch class details using the simple share API endpoint
        const response = await fetch(`${config.API_URL}/share/class/${classId}`);
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            // Redirect to the full class URL with group ID
            window.location.replace(`/#/group/${result.data.group_id}/classes/${classId}`);
          } else {
            setError('Class not found');
            setLoading(false);
          }
        } else {
          setError('Class not found');
          setLoading(false);
        }
      } catch (err) {
        console.error('Error fetching class:', err);
        setError('Failed to load class');
        setLoading(false);
      }
    };
    
    fetchClassGroup();
  }, [classId]);
  
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading class...</div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>{error}</div>
      </div>
    );
  }
  
  return null;
};

// Login route component for authentication
const LoginRoute = () => {
  const { currentUser, loading: authLoading } = useAuth();
  const [redirectPath, setRedirectPath] = useState(null);
  
  React.useEffect(() => {
    const handleRedirect = async () => {
      // Wait for auth to load
      if (authLoading) return;
      
      if (!currentUser) {
        // Not logged in, stay on auth page
        return;
      }
      
      // 🚨 CRITICAL FIX: Check if authentication came from DirectLinkAuth
      const isDirectLinkAuth = localStorage.getItem('directLinkAuthSuccess') === 'true';
      if (isDirectLinkAuth) {
        console.log('🔄 LoginRoute: Authentication from DirectLinkAuth detected, skipping auto-redirect');
        console.log('🔄 LoginRoute: User should stay on their original page');
        return;
      }
      
      // 🚨 NEW FIX: Check for redirect URL from DirectLinkAuth
      const redirectUrl = localStorage.getItem('redirectAfterAuth');
      if (redirectUrl) {
        console.log('🔄 LoginRoute: Found redirect URL from DirectLinkAuth:', redirectUrl);
        localStorage.removeItem('redirectAfterAuth'); // Clean up
        // Use hash-based navigation for hash router
        if (redirectUrl.startsWith('#')) {
          window.location.hash = redirectUrl.substring(1); // Remove the # and set hash
        } else {
          window.location.href = redirectUrl; // Fallback for full URLs
        }
        return;
      }
      
      // Check if we're coming from group creation success page
      const urlParams = new URLSearchParams(window.location.search);
      const isComingFromGroupCreation = urlParams.get('welcome') === 'true' || 
                                       urlParams.get('_fresh') || 
                                       window.location.hash.includes('group-creation-success');
      
      // If coming from group creation, don't auto-redirect - let the success page handle it
      if (isComingFromGroupCreation) {
        console.log('🔄 LoginRoute: Coming from group creation, skipping auto-redirect');
        return;
      }
      
      // Redirect based on user role - admins to dashboard, non-admins to app download
      if (currentUser?.role === 'admin' || currentUser?.isAdmin === true) {
        console.log('🔄 LoginRoute: Admin user, redirecting to /dashboard');
        setRedirectPath('/dashboard');
      } else {
        console.log('🔄 LoginRoute: Non-admin user, redirecting to /get-app (legacy dashboard sunset)');
        setRedirectPath('/get-app');
      }
    };
    
    handleRedirect();
  }, [currentUser, authLoading]);
  
  // If we have a redirect path, redirect now
  if (redirectPath) {
    return <Navigate to={redirectPath} replace />;
  }
  
  // Show loading or auth page
  if (authLoading || currentUser) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>;
  }
  
  return <AuthPage />;
};

// Inner layout component that has access to the router context
const LayoutWithNavigation = ({ children }) => {
  const { isAuthenticated, currentUser } = useAuth();
  const location = useLocation();
  
  // Define auth-related paths where we don't want to show nav
  const authPaths = [
    '/', 
    '/reset-password', 
    '/reset-password-confirm', 
    '/main-landing', 
    '/community-onboarding',
    '/signup',
    '/survey',
    '/candlelighting',
    '/candlelightingtimes',
    '/shababtends',
    '/fasttimesnow',
    '/fasttimes',
    '/shabbat',
    '/shabbattimes',
    '/Shabbat',
    '/Candletimes',
    '/Fastend',
    '/fastbegins',
    '/Tishabeav',
    '/get-app',
    '/login'
  ];
  
  // Check for dynamic paths
  const isDynamicAuthPath = location.pathname.startsWith('/names/') || 
                           location.pathname.startsWith('/name-claim-signup') ||
                           location.pathname.startsWith('/claim/') ||
                           location.pathname.startsWith('/demo/') ||
                           location.pathname.startsWith('/shabbat/');
  
  const isAuthPath = authPaths.includes(location.pathname) || isDynamicAuthPath;
  
  // Define legal pages that should not show navigation
  const legalPaths = ['/privacy-policy', '/terms-of-service'];
  const isLegalPage = legalPaths.includes(location.pathname);
  
  // Check if current path is an event, class, or membership details page
  const isDetailsPage = location.pathname.includes('/events/') || 
                         location.pathname.includes('/classes/') ||
                         location.pathname.includes('/memberships/');
                         
  // Check if the current path is any onboarding page (both the intro and the steps)
  const isOnboardingPage = location.pathname.includes('/onboarding') ||
                           location.pathname.match(/\/group\/.*\/onboarding$/);
  
  // Check if user is admin (admins use sidebar navigation, not bottom nav)
  const isAdmin = currentUser?.role === 'admin';
  
  // Only show bottom nav if user is authenticated AND not admin AND not on an auth page AND not on details pages AND not in onboarding AND not on legal pages
  const showBottomNav = isAuthenticated() && !isAdmin && !isAuthPath && !isDetailsPage && !isOnboardingPage && !isLegalPage;
  
  return (
    <>
      <div className={showBottomNav ? "content-with-bottom-nav" : ""}>
        {children}
      </div>
      {showBottomNav && <BottomNavigation />}
    </>
  );
};

// Dashboard route component to handle admin/member redirection
const DashboardRoute = () => {
  const { currentUser } = useAuth();
  
  console.log('🔀 DashboardRoute - currentUser:', currentUser);
  console.log('🔀 DashboardRoute - user role:', currentUser?.role);
  console.log('🔀 DashboardRoute - isAdmin:', currentUser?.isAdmin);
  
  // Check if user is admin
  if (currentUser?.role === 'admin' || currentUser?.isAdmin === true) {
    console.log('🔀 DashboardRoute - Redirecting admin to community feed dashboard');
    // Redirect to community feed page as default admin landing
    return <Navigate to="/admin/dashboard/updates" />;
  }
  
  // Sunset legacy member dashboard - redirect all non-admin users to app download page
  console.log('🔀 DashboardRoute - Non-admin user detected, redirecting to /get-app (legacy dashboard sunset)');
  return <Navigate to="/get-app" />;
};

// Profile route component to handle admin redirection
const ProfileRoute = () => {
  const { currentUser } = useAuth();
  return currentUser?.role === 'admin' ? <Navigate to="/account" /> : <MemberProfilePage />;
};



function App() {
  return (
    <Elements stripe={stripePromise}>
      <AuthProvider>
        <GroupProvider>
          <SnackbarProvider>
            <StripeConnectWrapper>
              <AppContent />
            </StripeConnectWrapper>
          </SnackbarProvider>
        </GroupProvider>
      </AuthProvider>
    </Elements>
  );
}

// Separate component to access auth context
const AppContent = () => {
  const { currentUser, tokenExpired, refreshSession, getCachedUserRole } = useAuth();
  const [refreshLoading, setRefreshLoading] = useState(false);
  
  // Handle session refresh when token is expired
  const handleSessionRefresh = async () => {
    setRefreshLoading(true);
    try {
      await refreshSession();
      
      // Check if user is on a group page - if so, stay on that page
      const currentPath = window.location.hash.substring(1); // Remove the # from hash
      const isOnGroupPage = currentPath.startsWith('/group/') || 
                           currentPath.includes('/events/') || 
                           currentPath.includes('/classes/') ||
                           currentPath.includes('/membership/');
      
      if (isOnGroupPage) {
        console.log('🔄 Session refreshed while on group page, staying on current page:', currentPath);
        // Don't redirect, stay on the current group page
        return;
      }
      
      // Always redirect to dashboard for both admins and members
      // The DashboardRoute component will handle showing the appropriate view
      window.location.href = '#/dashboard';
    } catch (error) {
      console.error('Session refresh failed:', error);
      // If refresh fails, redirect to login
      window.location.href = '#/login';
    } finally {
      setRefreshLoading(false);
    }
  };

  // Show token expired prompt if token is expired
  if (tokenExpired) {
    return (
      <TokenExpiredPrompt 
        onRefresh={handleSessionRefresh}
        userRole={getCachedUserRole()}
        loading={refreshLoading}
      />
    );
  }
  
  // Use user ID as key to force complete re-render when user changes
  // This prevents stale data from previous user sessions
  // NOTE: Don't use changing keys for Router as it resets navigation
  // const userKey = currentUser?.id || 'no-user';
  
  return (
    <Router>
      <PageTracker />
      <EmailVerificationHandler />
      <div className="App">
        <LayoutWithNavigation>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/search" element={<CommunitySearchPage />} />
            <Route path="/explore-prayers" element={<ExplorePrayersPage />} />
            <Route path="/minyan" element={<FindMinyanPage />} />
            <Route path="/minyan-share" element={<MinyanSharePage />} />
            <Route path="/gateway" element={<GatewayLanding />} />
            <Route path="/communities" element={<MainLanding />} />
            <Route path="/main-landing" element={<Navigate to="/communities" replace />} />
            <Route path="/waitlist-gift" element={<ExternalRedirect url="https://yesod.us" />} />
            <Route path="/community-onboarding" element={<CommunityOnboarding />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/survey" element={<Survey />} />
            <Route path="/pricing" element={<PricingPage />} />
            
            {/* Shabbat Times — dedicated page + SEO city slug routes */}
            <Route path="/shabbat" element={<ShabbatTimesPage />} />
            <Route path="/shabbat/:citySlug" element={<ShabbatTimesPage />} />
            <Route path="/Shabbat" element={<Navigate to="/shabbat" replace />} />
            <Route path="/shabbattimes" element={<Navigate to="/shabbat" replace />} />

            {/* Candle Lighting routes */}
            <Route path="/candlelighting" element={<AppDownloadLanding />} />
            <Route path="/candlelightingtimes" element={<AppDownloadLanding />} />
            <Route path="/shababtends" element={<AppDownloadLanding />} />
            <Route path="/fasttimesnow" element={<AppDownloadLanding />} />
            <Route path="/Candletimes" element={<AppDownloadLanding />} />
            
            {/* Candle Lighting Share Page - Shareable link with rich preview */}
            <Route path="/candle-lighting/:share_code" element={<CandleLightingSharePage />} />
            <Route path="/candle-lighting/:share_code/:city" element={<CandleLightingSharePage />} />
            
            {/* Fast Times routes (all use dedicated FastTimes component) */}
            <Route path="/fasttimes" element={<FastTimes />} />
            <Route path="/Fastend" element={<FastTimes />} />
            <Route path="/fastbegins" element={<FastTimes />} />
            <Route path="/Tishabeav" element={<FastTimes />} />
            
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/get-app" element={<GetAppPage />} />
            <Route path="/reset-password" element={<ResetPasswordRequest />} />
            <Route path="/reset-password-confirm" element={<ResetPasswordConfirm />} />
            
            {/* Prayer Chain Creation Flow - Multi-step mobile-first */}
            <Route path="/create-chain" element={<SelectDedicationPage />} />
            <Route path="/create-chain/type" element={<ChooseChainTypePage />} />
            <Route path="/create-chain/details" element={<WhoIsThisForPage />} />
            <Route path="/create-chain/story" element={<TellTheStoryPage />} />
            <Route path="/preview-chain/:shortCode" element={<PreviewChainPage />} />
            <Route path="/auth" element={<AuthGatePage />} />
            <Route path="/claim/:code/success" element={<ClaimSuccessPage />} />
            
            {/* Direct share link routes - Public access */}
            <Route path="/event/:eventId" element={<PublicEventRedirect />} />
            <Route path="/class/:classId" element={<PublicClassRedirect />} />
            
            {/* Group Claim Route - No auth required */}
            <Route path="/claim/:token" element={<GroupClaimPage />} />
            
            {/* Name Claim Route - No auth required */}
            <Route path="/names/:claimCode" element={<NameClaimPage />} />
            <Route path="/name-claim-signup" element={<NameClaimSignUp />} />
            
            {/* Demo Route for Testing UI */}
            <Route path="/demo/group-claim" element={<GroupClaimDemo />} />
            
            {/* Public Community Page - served by Express via dev-server proxy */}
            <Route path="/community/:id" element={<CommunityHardRedirect />} />
            <Route path="/group/:id/public" element={<PublicCommunityPage />} />
            
            {/* Post Details Page */}
            <Route path="/group/:id/post/:postId" element={<PostDetailsPage />} />
            
            {/* Legal Pages - Available to all users */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            
            {/* Test Route for shadcn/ui + Tailwind setup */}
            <Route path="/test-ui" element={<ShadcnTest />} />
            
            {/* Test Route for Modern Dashboard */}
            <Route path="/modern-dashboard" element={
              <PrivateRoute>
                <ModernDashboard />
              </PrivateRoute>
            } />
            
            {/* Test Route for Admin Sidebar */}
            <Route path="/admin-sidebar" element={
              <AdminRoute>
                <AdminSidebarTest />
              </AdminRoute>
            } />
            
            {/* Test Route for Admin Sidebar (No Auth Required) */}
            <Route path="/admin-sidebar-test" element={<AdminSidebarTest />} />
            
            
            <Route 
              path="/dashboard" 
              element={
                <PrivateRoute>
                  <DashboardRoute />
                </PrivateRoute>
              } 
            />
            <Route 
              path="/create-group" 
              element={
                <AdminRoute>
                  <CreateGroup />
                </AdminRoute>
              } 
            />
            <Route 
              path="/group-creation-success/:groupId" 
              element={
                <AdminRoute>
                  <GroupCreationSuccess />
                </AdminRoute>
              } 
            />
            <Route 
              path="/profile" 
              element={
                <PrivateRoute>
                  {/* Only allow members to access profile page, redirect admins to account */}
                  <ProfileRoute />
                </PrivateRoute>
              } 
            />
            <Route 
              path="/account" 
              element={
                <AdminRoute>
                  <AdminProfilePage />
                </AdminRoute>
              } 
            />
            <Route 
              path="/members" 
              element={
                <AdminRoute>
                  <MembersAdminPage />
                </AdminRoute>
              } 
            />
            
            {/* New Admin Dashboard Routes - Standardized Route-Based Navigation */}
            <Route 
              path="/admin/dashboard" 
              element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              } 
            />
            <Route 
              path="/admin/dashboard/members" 
              element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              } 
            />
            <Route 
              path="/admin/dashboard/events" 
              element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              } 
            />
            <Route 
              path="/admin/dashboard/classes" 
              element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              } 
            />
            <Route 
              path="/admin/dashboard/calendar" 
              element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              } 
            />
            <Route 
              path="/admin/dashboard/prayer" 
              element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              } 
            />
            <Route 
              path="/admin/dashboard/prayer-list" 
              element={
                <AdminRoute>
                  <PrayerListPage />
                </AdminRoute>
              } 
            />
            <Route 
              path="/admin/dashboard/programs" 
              element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              } 
            />
            <Route 
              path="/admin/dashboard/financials" 
              element={<Navigate to="/admin/dashboard/financials/payments" replace />} 
            />
            <Route 
              path="/admin/dashboard/financials/payments" 
              element={
                <AdminRoute>
                  <FinancialsPage />
                </AdminRoute>
              } 
            />
            <Route 
              path="/admin/dashboard/financials/pledges" 
              element={
                <AdminRoute>
                  <PledgesPage />
                </AdminRoute>
              } 
            />
            <Route 
              path="/admin/dashboard/financials/tickets" 
              element={
                <AdminRoute>
                  <TicketsPage />
                </AdminRoute>
              } 
            />
            <Route 
              path="/admin/dashboard/financials/memberships" 
              element={
                <AdminRoute>
                  <FinancialsPage />
                </AdminRoute>
              } 
            />
            <Route 
              path="/admin/dashboard/account" 
              element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              } 
            />
            <Route 
              path="/admin/dashboard/updates" 
              element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              } 
            />
            
            {/* Legacy Admin Routes - Redirect to New System */}
            <Route 
              path="/admin/members" 
              element={<Navigate to="/admin/dashboard/updates" replace />} 
            />
            <Route 
              path="/members" 
              element={
                <AdminRoute>
                  <Navigate to="/admin/dashboard/updates" replace />
                </AdminRoute>
              } 
            />
            <Route 
              path="/group/:id" 
              element={
                <PublicRoute>
                  <GroupPage />
                </PublicRoute>
              } 
            />
            <Route 
              path="/group/:id/events/:eventId" 
              element={
                <PublicRoute>
                  <EventDetails />
                </PublicRoute>
              } 
            />
            {/* Singular routes for share links */}
            <Route 
              path="/group/:id/event/:eventId" 
              element={
                <PublicRoute>
                  <EventDetails />
                </PublicRoute>
              } 
            />
            <Route 
              path="/saved" 
              element={
                <PrivateRoute>
                  <SavedPage />
                </PrivateRoute>
              } 
            />
            <Route 
              path="/admin/members" 
              element={
                <AdminRoute>
                  <MembersAdminPage />
                </AdminRoute>
              } 
            />
            <Route 
              path="/admin/dashboard/:id/members/:userId" 
              element={
                <AdminRoute>
                  <AdminMemberProfilePage />
                </AdminRoute>
              } 
            />
            <Route 
              path="/group/:id/members-admin" 
              element={
                <AdminRoute>
                  <MembersAdminPage />
                </AdminRoute>
              } 
            />
            <Route 
              path="/group/:id/members-admin/:userId" 
              element={
                <AdminRoute>
                  <AdminMemberProfilePage />
                </AdminRoute>
              } 
            />
            <Route 
              path="/group/:id/classes/:classId" 
              element={
                <PublicRoute>
                  <ClassDetails />
                </PublicRoute>
              } 
            />
            {/* Singular route for share links */}
            <Route 
              path="/group/:id/class/:classId" 
              element={
                <PublicRoute>
                  <ClassDetails />
                </PublicRoute>
              } 
            />
            <Route 
              path="/group/:id/membership-details" 
              element={
                <GroupRoute>
                  <MembershipDetails />
                </GroupRoute>
              } 
            />
            <Route 
              path="/group/:id/memberships/:membershipId" 
              element={
                <GroupRoute>
                  <MembershipDetails />
                </GroupRoute>
              } 
            />
            
            {/* Onboarding Routes */}
            <Route 
              path="/group/:groupId/onboarding" 
              element={
                <GroupRoute>
                  <OnboardingIntro />
                </GroupRoute>
              } 
            />
            <Route 
              path="/group/:groupId/onboarding/identity" 
              element={
                <GroupRoute>
                  <IdentityVerification />
                </GroupRoute>
              } 
            />
            <Route 
              path="/group/:groupId/onboarding/profile" 
              element={
                <GroupRoute>
                  <ProfileCreation />
                </GroupRoute>
              } 
            />
            <Route 
              path="/group/:groupId/onboarding/anniversary" 
              element={
                <GroupRoute>
                  <AnniversaryDatePage />
                </GroupRoute>
              } 
            />
            <Route 
              path="/group/:groupId/onboarding/kids" 
              element={
                <GroupRoute>
                  <KidsBirthday />
                </GroupRoute>
              } 
            />
            <Route 
              path="/group/:groupId/onboarding/yartzheits" 
              element={
                <GroupRoute>
                  <Yartzheits />
                </GroupRoute>
              } 
            />
            
            {/* Stripe Connect Settings */}
            <Route 
              path="/stripe/settings" 
              element={
                <AdminRoute>
                  <StripeConnectSettings />
                </AdminRoute>
              } 
            />
            
            {/* Add simplified onboarding routes without groupId for testing */}
            <Route path="/onboarding" element={<OnboardingIntro />} />
            <Route path="/onboarding/identity" element={<IdentityVerification />} />
            <Route path="/onboarding/profile" element={<ProfileCreation />} />
            <Route path="/onboarding/anniversary" element={<AnniversaryDatePage />} />
            <Route path="/onboarding/kids" element={<KidsBirthday />} />
            <Route path="/onboarding/yartzheits" element={<Yartzheits />} />
            
            {/* Group Invite Page - Accessible via invite link */}
            <Route path="/invite/group/:groupId" element={<GroupInvitePage />} />
          </Routes>
        </LayoutWithNavigation>
        
        {/* Global Snackbar Notification */}
        <GlobalSnackbar />
      </div>
    </Router>
  );
}

// Component to access snackbar context and render notification
const GlobalSnackbar = () => {
  const { isVisible, message, hideSnackbar } = useSnackbar();
  
  return (
    <SnackbarNotification
      isVisible={isVisible}
      message={message}
      onDismiss={hideSnackbar}
    />
  );
};

export default App;